// @flow
import axios from 'axios';
import BaseModel from 'Stores/BaseModel';
import ListModel from 'Stores/ListModel';
import AssetModel from 'Stores/AssetModel';
import {
  validateLength,
  validateNumber,
  isInvalid,
  injectUrlVars,
  validateNoLocalFootnotes,
} from 'Utils/Validators';
import { isAfter } from 'date-fns';
import { formatDate, formatPrice } from 'Utils';
import { withInheritQueryParam } from 'Utils/withInheritQueryParam';

type Price = {
  price: number,
  priceText: string,
  footnote: string,
  pricePrefix: string,
};

type Link = {
  id: number,
  title: string,
  class: string,
  link: string,
  target: string,
  url: string,
  additional: string,
  model: string,
  type: string,
};

export default class OfferModelDeprecated extends BaseModel {
  id: number = 0;
  sorting: number = 0;
  parentId: ?number = null;
  hidden: boolean = true;
  custom: boolean = true;
  offerType: string = 'PRIVATE';
  offerSubtype: string = null;
  customerBenefitPrice: number = -1;
  customerBenefitPriceText: string = '';
  carlineDeeplink: string = '';
  firstCta: string = '';
  secondCta: string = '';
  modelDescription: string = '';
  consumptionInfoDescription: string = '';
  range: number = 0;
  link: ?Link = null;
  link1: ?Link = null;
  image: ?AssetModel = null;
  description: string = '';
  starttime: ?Date = new Date();
  cashPrice: Price = {
    price: 0,
    priceText: '',
    footnote: '',
    pricePrefix: '',
  };
  financePrice: Price = {
    price: 0,
    priceText: '',
    footnote: '',
    pricePrefix: '',
  };
  leasingPrice: Price = {
    price: 0,
    priceText: '',
    footnote: '',
    pricePrefix: '',
  };

  constructor(data: Object = {}) {
    super();
    this.hydrate(data);

    this.image = this.image ? new AssetModel(this.image) : null;

    this.linkUrl1 = injectUrlVars(this.link1 && this.link1.href ? this.link1.href : '');
    this.linkTitle1 = this.link1 && this.link1.label ? this.link1.label : '';

    this.linkUrl = injectUrlVars(this.link && this.link.href ? this.link.href : '');
    this.linkTitle = this.link && this.link.label ? this.link.label : '';
  }

  valid = (opts = { noLocalFootnotes: false }) => {
    this.errors = {};

    if (opts.noLocalFootnotes) {
      validateNoLocalFootnotes(this.errors, 'description', this.description);
    }

    if (this.leasingPrice) {
      validateNumber(this.errors, 'leasingPricePrice', this.leasingPrice.price, 1, 9999);
      validateLength(this.errors, 'leasingPriceFootnote', this.leasingPrice.footnote, 1, 3000);
    }

    validateNumber(this.errors, 'customerBenefitPrice', this.customerBenefitPrice, 0, 99999);

    if (this.offerType === 'PRIVATE') {
      if (this.financePrice) {
        validateNumber(this.errors, 'financePricePrice', this.financePrice.price, 1, 9999);
        validateLength(this.errors, 'financePriceFootnote', this.financePrice.footnote, 1, 3000);
      }

      if (this.cashPrice) {
        validateNumber(this.errors, 'cashPricePrice', this.cashPrice.price, 1, 99999);
        validateLength(this.errors, 'cashPriceFootnote', this.cashPrice.footnote, 1, 3000);
      }
    }

    return !isInvalid(this.errors);
  };

  toListItem = () => {
    const subline = [];
    if (this.leasingPrice) {
      subline.push(`Leasing ${formatPrice(this.leasingPrice.price)}`);
    }
    if (this.offerType === 'PRIVATE') {
      if (this.cashPrice) {
        subline.push(`Barpreis ${formatPrice(this.cashPrice.price)}`);
      }
      if (this.financePrice) {
        subline.push(`Finanzierung ${formatPrice(this.financePrice.price)}`);
      }
    }
    if (this.isScheduled()) {
      subline.push(`Gültig ab: ${formatDate(this.starttime)}`);
    }

    return {
      id: this.id,
      index: this.sorting,
      headline:
        this.offerType === 'SPECIAL'
          ? `${this.modelDescription} (${this.offerSubtype})`
          : this.modelDescription,
      subline: subline.join(' | '),
      hidden: this.hidden,
      firstCta: this.firstCta,
      secondCta: this.secondCta,
      image: this.image,
      model: this,
    };
  };

  getFootnotes = () => {
    if ((this.offerType === 'BUSINESS' || this.offerType === 'SPECIAL') && this.leasingPrice) {
      return [this.leasingPrice.footnote];
    } else {
      return [
        this.cashPrice && this.cashPrice.footnote,
        this.leasingPrice && this.leasingPrice.footnote,
        this.financePrice && this.financePrice.footnote,
      ].filter(Boolean);
    }
  };

  hasError = (prop: string) => {
    return this.errors[prop] && this.errors[prop].length > 0;
  };

  getPriceErrors = () => {
    return [
      this.hasError('cashPricePrice') || this.hasError('cashPriceFootnote'),
      this.hasError('financePricePrice') ||
        this.hasError('financePricePriceText') ||
        this.hasError('financePriceFootnote'),
      this.hasError('leasingPricePrice') ||
        this.hasError('leasingPricePriceText') ||
        this.hasError('leasingPriceFootnote'),
    ];
  };

  save = (inherit?: boolean) =>
    axios
      .patch(withInheritQueryParam(`/api/v1/offers/${this.id}/`, inherit), this.toJSON())
      .then((res) => new OfferModelDeprecated(res.data));

  setCustomerBenefitPrice = (value: number) => {
    // MAZ-1457 Workaround for customerBenefitPrice: *set* in BaseModel doesn`t update first level property in Offer
    this.customerBenefitPrice = value;
  };

  toJSON = () => {
    return {
      id: this.id,
      sorting: this.sorting,
      parentId: this.parentId,
      firstCta: this.firstCta,
      secondCta: this.secondCta,
      hidden: this.hidden,
      custom: this.custom,
      offerType: this.offerType,
      image: this.image,
      modelDescription: this.modelDescription,
      carlineDeeplink: this.carlineDeeplink,
      description: this.description,
      consumptionInfoDescription: this.consumptionInfoDescription,
      range: this.range,
      cashPrice: this.cashPrice,
      financePrice: this.financePrice,
      leasingPrice: this.leasingPrice,
      link: this.link,
      offerSubtype: this.offerSubtype,
      customerBenefitPrice: this.customerBenefitPrice,
    };
  };

  isScheduled = () => {
    return isAfter(new Date(this.starttime), new Date());
  };

  static getAll = (params: Object = {}) => {
    return axios({
      method: 'get',
      url: '/api/v1/offers/',
      params,
    }).then((res: { data: Array<Object> }) => {
      const { data: offers = [] } = res;
      return new ListModel(
        offers.filter((offer) => !!offer.cashPrice || !!offer.leasingPrice || !!offer.financePrice),
        OfferModelDeprecated
      );
    });
  };
}
